import React, { createContext, useCallback, useEffect, useMemo, useReducer } from 'react';

// third-party
import { Chance } from 'chance';

// reducer - state management
import { LOGIN, LOGOUT } from 'contexts/auth-reducer/actions';
import authReducer from 'contexts/auth-reducer/auth';

// project-imports
import Loader from 'components/Loader';
import axios from 'utils/axios';
import { mergeAuthSnapshotFromEnvelope } from 'utils/mergeAuthUser';
import { notify } from 'utils/notify';
import { login as loginRequest, token as fetchSessionToken, forgotPassword as forgotPasswordRequest } from 'services/auth';

// types
import { AuthProps, JWTContextType } from 'types/auth';

const chance = new Chance();

// constant
const initialState: AuthProps = {
  isLoggedIn: false,
  isInitialized: false,
  user: null,
  token: null
};

const setSession = (token?: string | null) => {
  if (token) {
    localStorage.setItem('serviceToken', token);
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    localStorage.removeItem('serviceToken');
    localStorage.removeItem('refreshToken');
    delete axios.defaults.headers.common.Authorization;
  }
};

/** Login API returns `{ success, data: { token, user, ... } }` from `auth.login` — flatten here (auth.ts unchanged). */
function normalizeLoginEnvelope(raw: unknown): {
  token?: string;
  accessToken?: string;
  refreshToken?: string;
  user?: unknown;
} {
  if (!raw || typeof raw !== 'object') return {};
  const r = raw as {
    data?: Record<string, unknown>;
    token?: string;
    accessToken?: string;
    refreshToken?: string;
    user?: unknown;
  };
  if (r.data && typeof r.data === 'object') {
    const d = r.data;
    const mergedUser = mergeAuthSnapshotFromEnvelope(d) ?? (d.user ?? r.user);
    return {
      token: (d.token as string | undefined) ?? r.token,
      accessToken: (d.accessToken as string | undefined) ?? r.accessToken,
      refreshToken: (d.refreshToken as string | undefined) ?? r.refreshToken,
      user: mergedUser,
    };
  }
  const flatUser = mergeAuthSnapshotFromEnvelope(r) ?? r.user;
  return {
    token: r.token,
    accessToken: r.accessToken,
    refreshToken: r.refreshToken,
    user: flatUser,
  };
}

// ==============================|| JWT CONTEXT & PROVIDER ||============================== //

const JWTContext = createContext<JWTContextType | null>(null);

export const JWTProvider = ({ children }: Readonly<{ children: React.ReactElement }>) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    const init = async () => {
      try {
        const serviceToken = window.localStorage.getItem('serviceToken');
        if (!serviceToken) {
          setSession(null);
          dispatch({ type: LOGOUT });
          return;
        }

        // Page refresh: always hit token API if anything is stored (do not gate on local JWT exp).
        setSession(serviceToken);
        const data = await fetchSessionToken();
        const inner = (data as { data?: Record<string, unknown> }).data ?? (data as Record<string, unknown>);
        const user =
          mergeAuthSnapshotFromEnvelope(inner) ??
          (inner && typeof inner === 'object' && 'user' in inner && inner.user && typeof inner.user === 'object'
            ? (inner.user as AuthProps['user'])
            : null);
        const nextAccess =
          (inner as { token?: string }).token ??
          (inner as { accessToken?: string }).accessToken ??
          (inner as { access_token?: string }).access_token ??
          serviceToken;
        if (nextAccess && nextAccess !== serviceToken) {
          setSession(nextAccess);
        }
        dispatch({
          type: LOGIN,
          payload: {
            isLoggedIn: true,
            user: user ?? null,
            token: nextAccess ?? serviceToken
          }
        });
      } catch (err) {
        console.error(err);
        setSession(null);
        dispatch({
          type: LOGOUT
        });
      }
    };

    void init();
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    try {
      const raw = await loginRequest({ email, password });
      const result = normalizeLoginEnvelope(raw);
      const access =
        result.token ??
        result.accessToken ??
        (result as { access_token?: string }).access_token;
      if (!access || typeof access !== 'string') {
        throw new Error('Login did not return an access token (expected token or accessToken in response).');
      }
      setSession(access);
      if (result.refreshToken) {
        window.localStorage.setItem('refreshToken', result.refreshToken);
      }
      dispatch({
        type: LOGIN,
        payload: {
          isLoggedIn: true,
          user: result.user as AuthProps['user'],
          token: access
        }
      });
    } catch (error: unknown) {
      const err = error as { response?: { data?: unknown } };
      throw err?.response?.data ?? error;
    }
  }, []);

  const register = useCallback(async (email: string, password: string, firstName: string, lastName: string) => {
    const id = chance.bb_pin();
    const response = await axios.post('/api/account/register', {
      id,
      email,
      password,
      firstName,
      lastName
    });
    let users = response.data;

    if (window.localStorage.getItem('users') !== undefined && window.localStorage.getItem('users') !== null) {
      const localUsers = window.localStorage.getItem('users');
      users = [
        ...JSON.parse(localUsers!),
        {
          id,
          email,
          password,
          name: `${firstName} ${lastName}`
        }
      ];
    }

    window.localStorage.setItem('users', JSON.stringify(users));
  }, []);

  const logout = useCallback(() => {
    notify.success('Logged out successfully.');
    setSession(null);
    dispatch({ type: LOGOUT });
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    try {
      await forgotPasswordRequest(email);
    } catch (error: unknown) {
      const err = error as { response?: { data?: unknown } };
      throw err?.response?.data ?? error;
    }
  }, []);

  const updateProfile = useCallback(() => {}, []);

  const contextValue = useMemo(
    () => ({ ...state, login, logout, register, resetPassword, updateProfile }),
    [state, login, logout, register, resetPassword, updateProfile]
  );

  if (state.isInitialized !== undefined && !state.isInitialized) {
    return <Loader />;
  }

  return <JWTContext.Provider value={contextValue}>{children}</JWTContext.Provider>;
};

export default JWTContext;
